/**
 * Tasks - Clean task management with assignment
 */

import { useState } from 'react'
import { Check, Plus, Clock, User, Bell } from 'lucide-react'
import { cn } from '../lib/utils'

interface Task {
  id: string
  title: string
  assignee: string | null
  assignedBy: string
  dueDate: string | null
  priority: 'low' | 'medium' | 'high'
  status: 'todo' | 'in_progress' | 'done'
  reminder?: string
}

const TEAM = ['Maya P.', 'Carlos R.', 'Yuki T.', 'Hans M.', 'Alex C.']

const MOCK_TASKS: Task[] = [
  { id: '1', title: 'Review consciousness module docs', assignee: 'Alex C.', assignedBy: 'Maya P.', dueDate: 'Today', priority: 'high', status: 'in_progress' },
  { id: '2', title: 'Fix Guardian latency issue', assignee: 'Carlos R.', assignedBy: 'Alex C.', dueDate: 'Tomorrow', priority: 'high', status: 'todo', reminder: '2h before' },
  { id: '3', title: 'Update API documentation', assignee: null, assignedBy: 'System', dueDate: 'Jan 20', priority: 'medium', status: 'todo' },
  { id: '4', title: 'Refactor memory core tests', assignee: 'Yuki T.', assignedBy: 'Alex C.', dueDate: 'Jan 22', priority: 'low', status: 'todo' },
  { id: '5', title: 'Deploy v2.0 to staging', assignee: 'Alex C.', assignedBy: 'Maya P.', dueDate: null, priority: 'medium', status: 'done' },
]

export default function Tasks() {
  const [tasks, setTasks] = useState(MOCK_TASKS)
  const [filter, setFilter] = useState<'all' | 'mine' | 'assigned'>('all')
  const [showNewTask, setShowNewTask] = useState(false)
  const [newTask, setNewTask] = useState<{ title: string; assignee: string; priority: 'low' | 'medium' | 'high' }>({ title: '', assignee: '', priority: 'medium' })

  const currentUser = 'Alex C.'

  const filtered = tasks.filter(t => {
    if (filter === 'mine') return t.assignee === currentUser
    if (filter === 'assigned') return t.assignedBy === currentUser && t.assignee !== currentUser
    return true
  })

  const pending = filtered.filter(t => t.status !== 'done')
  const done = filtered.filter(t => t.status === 'done')

  const toggleTask = (id: string) => {
    setTasks(prev => prev.map(t =>
      t.id === id ? { ...t, status: t.status === 'done' ? 'todo' as const : 'done' as const } : t
    ))
  }

  const addTask = () => {
    if (!newTask.title.trim()) return
    const task: Task = {
      id: Date.now().toString(),
      title: newTask.title,
      assignee: newTask.assignee || null,
      assignedBy: currentUser,
      dueDate: null,
      priority: newTask.priority,
      status: 'todo'
    }
    setTasks(prev => [task, ...prev])
    setNewTask({ title: '', assignee: '', priority: 'medium' })
    setShowNewTask(false)
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light text-white">Tasks</h1>
          <p className="text-gray-500 mt-1">{pending.length} pending</p>
        </div>

        <div className="flex items-center gap-4">
          {/* Filter */}
          <div className="flex gap-1 bg-white/5 p-1 rounded-lg">
            {[
              { key: 'all', label: 'All' },
              { key: 'mine', label: 'My tasks' },
              { key: 'assigned', label: 'Assigned' }
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key as typeof filter)}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-md transition-colors',
                  filter === f.key ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowNewTask(true)}
            className="flex items-center gap-2 px-4 py-2 bg-lambda-gold text-black rounded-lg hover:bg-lambda-gold/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">New task</span>
          </button>
        </div>
      </div>

      {/* New Task Input */}
      {showNewTask && (
        <div className="p-4 bg-white/5 rounded-xl border border-white/10 space-y-4">
          <input
            type="text"
            placeholder="What needs to be done?"
            value={newTask.title}
            onChange={e => setNewTask(prev => ({ ...prev, title: e.target.value }))}
            className="w-full bg-transparent text-white placeholder-gray-500 text-lg outline-none"
            autoFocus
          />
          <div className="flex items-center gap-4">
            <select
              value={newTask.assignee}
              onChange={e => setNewTask(prev => ({ ...prev, assignee: e.target.value }))}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"
            >
              <option value="">Unassigned</option>
              {TEAM.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <select
              value={newTask.priority}
              onChange={e => setNewTask(prev => ({ ...prev, priority: e.target.value as Task['priority'] }))}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            <div className="flex-1" />
            <button
              onClick={() => setShowNewTask(false)}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={addTask}
              className="px-4 py-2 bg-lambda-gold text-black rounded-lg hover:bg-lambda-gold/90 transition-colors"
            >
              Add task
            </button>
          </div>
        </div>
      )}

      {/* Task List */}
      <div className="space-y-2">
        {pending.map(task => (
          <TaskRow key={task.id} task={task} onToggle={() => toggleTask(task.id)} />
        ))}
      </div>

      {/* Completed */}
      {done.length > 0 && (
        <div className="pt-8 border-t border-white/5">
          <h2 className="text-sm text-gray-500 uppercase tracking-wider mb-4">Completed</h2>
          <div className="space-y-2 opacity-50">
            {done.map(task => (
              <TaskRow key={task.id} task={task} onToggle={() => toggleTask(task.id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function TaskRow({ task, onToggle }: { task: Task; onToggle: () => void }) {
  return (
    <div className="group flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] border border-white/5 transition-colors">
      {/* Checkbox */}
      <button
        onClick={onToggle}
        className={cn(
          'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
          task.status === 'done'
            ? 'bg-emerald-500 border-emerald-500'
            : 'border-gray-600 hover:border-gray-400'
        )}
      >
        {task.status === 'done' && <Check className="w-3 h-3 text-white" />}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          'text-white',
          task.status === 'done' && 'line-through text-gray-500'
        )}>
          {task.title}
        </p>
        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
          {task.assignee && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {task.assignee}
            </span>
          )}
          {task.dueDate && (
            <span className={cn(
              'flex items-center gap-1',
              task.dueDate === 'Today' && 'text-lambda-gold'
            )}>
              <Clock className="w-3 h-3" />
              {task.dueDate}
            </span>
          )}
          {task.reminder && (
            <span className="flex items-center gap-1 text-purple-400">
              <Bell className="w-3 h-3" />
              {task.reminder}
            </span>
          )}
        </div>
      </div>

      {/* Priority */}
      <span className={cn(
        'w-2 h-2 rounded-full',
        task.priority === 'high' && 'bg-red-500',
        task.priority === 'medium' && 'bg-yellow-500',
        task.priority === 'low' && 'bg-gray-500'
      )} />
    </div>
  )
}
